import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { SystemOrAccountRegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { UserRegisterOptions } from '../models/user-register-options';
import { UserRegisterListItem } from '../models/user-register-results.model';
import { UsersFilterOption } from '../models/users-filter-option';
import * as userRegisterActions from './user-register.actions';

export const userRegisterFeatureKey = 'userRegister';

export interface UserRegisterState
   extends SystemOrAccountRegisterState<UserRegisterListItem, UserRegisterOptions, OrderingOption> {
   filters: UsersFilterOption[];
   includeInactive: boolean;
}

const adapter: RegisterAdapter<UserRegisterListItem, UserRegisterOptions, OrderingOption> = createRegisterAdapter<
   UserRegisterListItem,
   UserRegisterOptions,
   OrderingOption
>({
   selectId: (userRegisterItem) => {
      return userRegisterItem.userId ?? 0;
   },
});

export const initialState: UserRegisterState = {
   ...adapter.getInitialState(),
   ...adapter.getInitialState(),
   filters: [],
   includeInactive: false,
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'name' }],
   isOnSystemPage: false,
};

export const userRegisterReducer = adapter.createReducer(
   userRegisterActions.userRegisterAdapterActions,
   initialState,
   on(userRegisterActions.userRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(userRegisterActions.userRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
         users: action.items,
      };
   }),
   on(userRegisterActions.userRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(userRegisterActions.userRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(userRegisterActions.roleUsersInitialQuickFilter, (state, action) => {
      return {
         ...state,
         quickFilters: {
            ...state.quickFilters,
            showAllUsers: false,
            searchText: state.quickFilters?.searchText ?? '',
            includeInactive: state.quickFilters?.includeInactive ?? false,
         },
      };
   }),
   on(userRegisterActions.userRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter(
                       (option) =>
                          !action.orderingOptions.some((newOption: OrderingOption) => newOption.field === option.field)
                    )
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(userRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: action.orderingOptions,
      };
   }),
   on(userRegisterActions.userRegisterAdapterActions.clearRegisterFilters, (state) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
         includeInactive: initialState.includeInactive,
         orderingOptions: initialState.orderingOptions,
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
      };
   }),
   on(userRegisterActions.loadSystemUsersSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: true,
      };
   }),
   on(userRegisterActions.loadAccountUsersSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: false,
      };
   })
);
export const userRegisterSelector = createFeatureSelector<UserRegisterState>(userRegisterFeatureKey);
export const selectors = adapter.getSelectors(userRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(userRegisterSelector);
